import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Button, Stack, Chip, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const CARD_SX = {
  p: 2,
  height: "100%",
  borderRadius: 3,
  boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  border: "1px solid #eef0f4",
};

function Stat({ label, value }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" fontWeight={700} sx={{ color: "#111827", lineHeight: 1.2 }}>{value ?? "—"}</Typography>
      <Typography variant="caption" sx={{ color: "#6b7280" }}>{label}</Typography>
    </Box>
  );
}

/**
 * Resource Allocation at a glance: what the live groups have been given and how students are
 * keeping up, plus the course / mobile plan split. Figures come from the Course Content report
 * (admin/analytics/course-content); without analytics access the card just links out.
 */
export default function ResourceAllocationCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("admin/analytics/course-content")
      .then((r) => { if (!cancelled) setData(r.data?.data || null); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  const s = data?.summary;
  const a = data?.audience;

  return (
    <Paper sx={{ ...CARD_SX, display: "flex", flexDirection: "column" }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>Course content</Typography>
      <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
        Worksheets, files and deadlines across live groups
      </Typography>

      {s ? (
        <>
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
            <Stat label="Worksheets allocated" value={s.worksheets_allocated} />
            <Stat label="Course files" value={s.files_allocated} />
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
            <Stat label="Completion" value={s.completion_percent == null ? "—" : `${s.completion_percent}%`} />
            <Stat label="Overdue" value={s.overdue} />
          </Stack>
          {a && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: "#374151" }}>Students</Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                <Chip size="small" label={`Course ${a.course_only}`} />
                <Chip size="small" label={`Mobile plan ${a.plan_only}`} />
                <Chip size="small" color="primary" label={`Both ${a.course_and_plan}`} />
              </Box>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
          Allocate worksheets, files, mock exams and past papers to each group's weeks or sessions.
        </Typography>
      )}

      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={() => nav("/admin/resource-allocation")}
          sx={{ bgcolor: "#7c3aed", textTransform: "none", fontWeight: 600 }}
        >
          Allocate
        </Button>
        {s && (
          <Button
            variant="outlined"
            onClick={() => nav("/admin/analytics/course-content")}
            sx={{ textTransform: "none", fontWeight: 600, borderColor: "#7c3aed", color: "#7c3aed" }}
          >
            View report
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
